import { sectionDraftChangesNode } from "@repo/app-types/entities/configurationAgent";
import type {
  PlaybookId,
  PlaybookSectionNode,
} from "@repo/app-types/entities/playbook";
import type { SectionProposalDetail } from "@repo/app-types/entities/playbookProposalDetail";
import type { FeedbackWithCurrentGrade } from "@repo/app-types/entities/recordingAssessmentFeedback";
import type { PublishNodeDisclosure } from "@repo/app-types/helpers/playbookNodePublish";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconSparkles, IconTrash } from "@tabler/icons-react";
import { type FC, useState } from "react";
import { twMerge } from "tailwind-merge";
import {
  isRemovalSampleToolResultEntry,
  isSectionSampleToolResultEntry,
  type SampleToolResultEntry,
  type SampleToolResultSectionEntry,
} from "../../hooks/useSampleToolResults.js";
import { CopilotProposalBanner } from "./copilotProposalBanner.js";
import { calculateSectionFeedbackAccuracyWithCurrentGrades } from "./feedbackAccuracyHelpers.js";
import { GraderFeedbackSlideoverContainer } from "./graderFeedbackSlideoverContainer.js";
import { PublishNodeConfirmationModal } from "./publishPlaybookPartialChangeConfirmationModal.js";
import { SectionForm } from "./sectionForm.js";
import { SectionSuggestionResults } from "./sectionSuggestionResults.js";

export interface RecordingAssessmentSectionSettingsProps {
  section: PlaybookSectionNode;
  /** Live (published) version of this section, if any, for showing field-level diffs. */
  liveSection?: PlaybookSectionNode;
  /**
   * Called on in-form edits. Optional — suggestion mode renders the form
   * read-only and never invokes this, and ghost sections don't need an
   * in-place edit path.
   */
  onUpdateSection?: (section: PlaybookSectionNode) => void;
  feedback: readonly FeedbackWithCurrentGrade[];
  isReadOnly?: boolean;
  /**
   * Copilot's unresolved proposal for this section, if any — a node holds one.
   * Its own action decides the render: a `delete` shows the removal banner
   * over an untouched form, an edit shows the merged section read-only with
   * the Results pane, and a check-only run shows Results with no banner.
   */
  proposal?: SampleToolResultEntry;
  /**
   * Accept the proposal. For an edit this carries the section as it would
   * stand once the draft is merged in — what the caller writes to the tree.
   * A removal carries nothing, having nothing to write.
   */
  onApplyProposal?: (mergedSection?: PlaybookSectionNode) => void;
  /** Reject it, leaving the section as it is. */
  onDiscardProposal?: () => void;
  /** Hard-delete this section from the playbook tree. */
  onDelete?: () => void;
  /** Ask the AI assistant to optimize this section. */
  onOptimizeSection?: (section: PlaybookSectionNode) => void;
  /** Ask the AI assistant to run the sample-applicability tool against this section. */
  onTestSection?: (section: PlaybookSectionNode) => void;
  /** Whether an assistant run is in progress (gates Optimize/Test). */
  isOptimizingSectionOrCriterion?: boolean;
  /** Absent where inline publishing isn't offered. */
  onPublishNode?: () => void;
  /** What a publish would and wouldn't carry; gates the consent modal. */
  publishDisclosure?: PublishNodeDisclosure;
  /** This node differs from live; otherwise the Publish button explains why it's disabled. */
  hasChangesToPublish?: boolean;
  /** Another publish or discard is in flight. */
  isPublishBlocked?: boolean;
  isPublishing?: boolean;
  playbookId?: PlaybookId | null;
}

export const RecordingAssessmentSectionSettings: FC<
  RecordingAssessmentSectionSettingsProps
> = ({
  section,
  liveSection,
  onUpdateSection,
  isReadOnly = false,
  feedback,
  proposal,
  onApplyProposal,
  onDiscardProposal,
  onDelete,
  onOptimizeSection,
  onTestSection,
  isOptimizingSectionOrCriterion = false,
  onPublishNode,
  publishDisclosure,
  hasChangesToPublish = false,
  isPublishBlocked = false,
  isPublishing,
  playbookId,
}) => {
  const [showFeedbackSlideover, setShowFeedbackSlideover] = useState(false);
  const [showPublishConfirmation, setShowPublishConfirmation] = useState(false);
  // Consent is asked only when there's something to disclose: the publish
  // pulls in a new section, or the draft holds changes it can't carry.
  const publishNeedsConfirmation =
    publishDisclosure !== undefined &&
    (publishDisclosure.newAncestorSectionCount > 0 ||
      publishDisclosure.globalOnlyChanges.length > 0);

  const updateSection = (updates: Partial<PlaybookSectionNode>) => {
    onUpdateSection?.({ ...section, ...updates });
  };

  const feedbackAccuracy = calculateSectionFeedbackAccuracyWithCurrentGrades(
    section.id,
    feedback,
  );

  // A removal has no draft and no measurements; an edit has both.
  const isRemovalProposal =
    proposal !== undefined && isRemovalSampleToolResultEntry(proposal);
  const suggestion =
    proposal !== undefined && isSectionSampleToolResultEntry(proposal)
      ? proposal
      : undefined;

  const isSuggestionMode =
    suggestion !== undefined && isSectionInSuggestionMode(section, suggestion);

  // Show the Results pane only when this section has a matching sample-tool
  // result (a proposal or a check-only Test run). `isSuggestionMode` already
  // implies a suggestion exists, so suggestion mode always shows the pane.
  const hasResults = suggestion !== undefined;

  // Read-only mode (e.g. version-history view): keep the historical
  // single-column form, no Results pane.
  if (isReadOnly) {
    return (
      <div className="space-y-4 p-2">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground">Section</h3>
        </div>
        <SectionForm
          section={section}
          liveSection={liveSection}
          onUpdateSection={updateSection}
          isReadOnly
        />
      </div>
    );
  }

  const displaySection = isSuggestionMode
    ? mergeSuggestionIntoSection(section, suggestion.candidate_draft)
    : section;
  const diffBaseline = isSuggestionMode ? section : liveSection;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {isRemovalProposal ? (
        <CopilotProposalBanner
          message="Copilot suggests removing this section, and the criteria inside it."
          isDestructive
          discardLabel="Keep it"
          confirmLabel="Remove section"
          onDiscard={() => onDiscardProposal?.()}
          onConfirm={() => onApplyProposal?.()}
        />
      ) : null}
      {isSuggestionMode ? (
        <CopilotProposalBanner
          message="Showing changes Copilot suggests."
          discardLabel="Discard changes"
          confirmLabel="Apply changes"
          onDiscard={() => onDiscardProposal?.()}
          onConfirm={() => onApplyProposal?.(displaySection)}
        />
      ) : null}
      <div
        className={twMerge(
          "grid min-h-0 flex-1 grid-cols-1 gap-x-2",
          hasResults && "md:grid-cols-[1fr_1px_1fr]",
        )}
      >
        <div className="flex min-h-0 flex-col p-2">
          <div className="flex min-h-0 flex-1 flex-col space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground">
                Section
              </h3>
              {!isSuggestionMode && feedbackAccuracy ? (
                <Button
                  variant={ButtonVariant.Tertiary}
                  size={Size.MEDIUM}
                  onClick={() => setShowFeedbackSlideover(true)}
                >
                  View feedback
                </Button>
              ) : null}
            </div>
            <SectionForm
              section={displaySection}
              liveSection={diffBaseline}
              onUpdateSection={updateSection}
              isReadOnly={isSuggestionMode}
            />
            {!isSuggestionMode ? (
              <GraderFeedbackSlideoverContainer
                isOpen={showFeedbackSlideover}
                onClose={() => setShowFeedbackSlideover(false)}
                feedbacks={feedback}
                node={section}
              />
            ) : null}
          </div>
          {!isSuggestionMode ? (
            <div className="mt-auto flex items-center justify-between pt-4">
              <div className="flex items-center gap-2">
                {onDelete ? (
                  <IconButton
                    variant={ButtonVariant.Warning}
                    size={Size.MEDIUM}
                    icon={<IconTrash className="size-4" />}
                    accessibleLabel="Delete"
                    onClick={onDelete}
                  />
                ) : null}
                {onOptimizeSection ? (
                  <Button
                    variant={ButtonVariant.Secondary}
                    size={Size.MEDIUM}
                    icon={<IconSparkles className="size-4" />}
                    onClick={() => onOptimizeSection(section)}
                    isDisabled={
                      isOptimizingSectionOrCriterion || !section.name.trim()
                    }
                  >
                    Optimize
                  </Button>
                ) : null}
                {onTestSection ? (
                  <Button
                    variant={ButtonVariant.Secondary}
                    size={Size.MEDIUM}
                    onClick={() => onTestSection(section)}
                    isDisabled={
                      isOptimizingSectionOrCriterion || !section.name.trim()
                    }
                  >
                    Test
                  </Button>
                ) : null}
              </div>
              {onPublishNode ? (
                // A disabled button fires no hover events, so the tooltip hangs off a wrapper.
                <TooltipTrigger isDisabled={hasChangesToPublish}>
                  <TooltipHoverTarget
                    shape="rounded"
                    isDisabled={hasChangesToPublish}
                  >
                    <Button
                      variant={ButtonVariant.Primary}
                      size={Size.MEDIUM}
                      onClick={
                        publishNeedsConfirmation
                          ? () => setShowPublishConfirmation(true)
                          : onPublishNode
                      }
                      isDisabled={isPublishBlocked || !hasChangesToPublish}
                      isLoading={isPublishing}
                    >
                      Publish this section
                    </Button>
                  </TooltipHoverTarget>
                  <SimpleTooltip>No changes to publish</SimpleTooltip>
                </TooltipTrigger>
              ) : null}
              {onPublishNode && publishDisclosure ? (
                <PublishNodeConfirmationModal
                  isOpen={showPublishConfirmation}
                  onOpenChange={setShowPublishConfirmation}
                  nodeLabel="section"
                  disclosure={publishDisclosure}
                  onConfirm={onPublishNode}
                />
              ) : null}
            </div>
          ) : null}
        </div>
        {hasResults ? (
          <>
            <div
              aria-hidden
              className={twMerge(
                "-mb-2 hidden bg-gray-200 md:block",
                !isSuggestionMode && "-mt-2",
              )}
            />
            <div className="flex min-h-0 flex-col gap-4 p-2">
              <h3 className="shrink-0 text-base font-semibold text-foreground">
                Results
              </h3>
              <div className="min-h-0 flex-1 overflow-y-auto p-1">
                <SectionSuggestionResults
                  aggregates={suggestion?.aggregates}
                  suggestion={suggestion}
                  playbookId={playbookId}
                  playbookNodeId={displaySection.id}
                />
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

/**
 * For an existing-section edit suggestion, merge populated `candidate_draft`
 * fields into the current section so the read-only form renders the proposed
 * end-state.
 */
function mergeSuggestionIntoSection(
  current: PlaybookSectionNode,
  candidateDraft: SectionProposalDetail["candidate_draft"],
): PlaybookSectionNode {
  return {
    ...current,
    ...(candidateDraft.name !== undefined ? { name: candidateDraft.name } : {}),
    ...(candidateDraft.description !== undefined
      ? { description: candidateDraft.description }
      : {}),
  };
}

/**
 * For a `create_section` ghost proposal, synthesize a full `PlaybookSectionNode`
 * from the saved `candidate_draft`. Callers can pass the result as the
 * `section` prop alongside `suggestion` to render the ghost in the same shell.
 */
export function buildNewSectionFromSuggestion(
  suggestion: SampleToolResultSectionEntry,
): PlaybookSectionNode {
  const { candidate_draft } = suggestion;
  return {
    type: "section",
    id: suggestion.playbook_node_id,
    name: candidate_draft.name ?? "",
    description: candidate_draft.description ?? "",
    nodes: [],
  };
}

/**
 * True when the section should render with the suggestion banner + read-only
 * form + Results pane. A "check-only" run — where the agent re-tested the
 * section without proposing edits — does not count.
 */
export function isSectionInSuggestionMode(
  section: PlaybookSectionNode,
  suggestion: SampleToolResultSectionEntry | undefined,
): boolean {
  if (!suggestion) return false;
  if (suggestion.kind === "create_section") return true;
  return sectionDraftChangesNode(section, suggestion.candidate_draft);
}
